/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React, { useEffect } from "react";
import FormFader from "@/src/components/Form/Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import FormSubmit from "@/src/components/Form/Common/FormSubmit/FormSubmit";
import _slice from "lodash/slice";
import ValidatorSelectField from "@/src/components/FormFields/ValidatorSelectField";
import { requiredValidator } from "@/src/services/validationRules";
import { Box, MenuItem } from "@mui/material";
import _cloneDeep from "lodash/cloneDeep";
import { useForm } from "@/src/provider/Form/FormProvider";
import _toNumber from "lodash/toNumber";
import Group from "@/src/components/Wizard/Group";
import CrossIcon from "@/src/assets/icons/common/toggleable/IconClose";
import CheckIcon from "@/src/assets/icons/common/toggleable/IconCheck";
import SpecialBurden from "./SpecialBurden/SpecialBurden";
import Typography from "@mui/material/Typography";
import FormSubmitProps from "@/src/types/FormSubmitProps";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";
import wizardStyle from "@/src/components/Form/wizardStyle";

type SpecialBurdensProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
};

export default function SpecialBurdens({ submitProps, product }: SpecialBurdensProps) {
  const { values, handleChange, handleSubmit } = useForm();

  useEffect(() => {
    const numberOfSpecialFinancialBurdens = values.numberOfSpecialFinancialBurdens ? _toNumber(values.numberOfSpecialFinancialBurdens) : 0;
    if (values.specialFinancialBurdens.length === numberOfSpecialFinancialBurdens) {
      return;
    }
    let specialFinancialBurdens = _cloneDeep(values.specialFinancialBurdens);
    for (let i = specialFinancialBurdens.length; i < Math.min(numberOfSpecialFinancialBurdens, 5); i++) {
      specialFinancialBurdens.push({
        description: "",
        petitionerShare: "",
      });
    }
    specialFinancialBurdens = _slice(specialFinancialBurdens, 0, numberOfSpecialFinancialBurdens);
    handleChange({
      target: {
        type: "other",
        name: "specialFinancialBurdens",
        value: specialFinancialBurdens,
      },
    });
  }, [values.specialFinancialBurdens, values.numberOfSpecialFinancialBurdens]);

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={wizardStyle.wizardForm} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Ausgaben
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "center",
            }}
          >
            <Grid size={12}>
              <Group
                type={"radio"}
                name={"hasSpecialFinancialBurdens"}
                question={"Haben Sie andere besondere finanzielle Belastungen, die bisher nicht abgefragt wurden?"}
                modalContent={
                  "Hierzu zählen z.B. Mehrausgaben für Schwangere, Alleinerziehende, Kranke und körperbehinderte Angehörige. Dies sind zum Beispiel besondere Kosten für Medikamente, Ernährung oder einen Rollstuhl."
                }
                options={[
                  {
                    labelText: "Nein",
                    labelIcon: <CrossIcon />,
                    labelIconActive: <CrossIcon active />,
                    value: "no",
                  },
                  {
                    labelText: "Ja",
                    labelIcon: <CheckIcon />,
                    labelIconActive: <CheckIcon active />,
                    value: "yes",
                  },
                ]}
                validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
              />
            </Grid>
            {values.hasSpecialFinancialBurdens === "yes" && (
              <>
                <ValidatorSelectField
                  label="Wie viele besondere finanzielle Belastungen haben Sie?"
                  name="numberOfSpecialFinancialBurdens"
                  validators={[requiredValidator("Bitte geben Sie an wie viele besondere finanzielle Belastungen Sie haben")]}
                >
                  <MenuItem value={"1"}>1</MenuItem>
                  <MenuItem value={"2"}>2</MenuItem>
                </ValidatorSelectField>
                {values.specialFinancialBurdens?.map((specialFinancialBurden: any, index: number) => (
                  <SpecialBurden key={index} index={index} path={`specialFinancialBurdens[${index}]`} product={product} />
                ))}
              </>
            )}
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
